import { useEffect, useState } from 'react';

import { Box, Divider, Stack, Typography, tokens } from '@cast/design-system';
import { Cloud } from '@cast/shared-types';

import { AgentCodeSnippet } from '@oneclick/components/code/code';
import { useAssetDiscoveryCommandQuery } from '@oneclick/hooks/queries/inventory';

import { Permissions } from './_components/Permissions';
import { ProviderSelect } from './_components/ProviderSelect';
import { ScopeToggle } from './_components/ScopeToggle';
import { providerToPermissions } from './_constants';
import { ScriptMethodProvider } from './_types';
import { useCloudConnectConfig } from './_hooks/useCloudConnectConfig';
import { StepBadge } from './StepBadge';
import { OnScriptStateChange, ONBOARDING_STEP_COPY } from '../../../_constants';

type CloudConnectProps = {
  onScriptStateChange?: OnScriptStateChange;
  onProviderChange?: (provider: Cloud) => void;
};

export const CloudConnect = ({
  onScriptStateChange,
  onProviderChange,
}: CloudConnectProps) => {
  const [provider, setProvider] = useState<Cloud>(Cloud.aws);

  const { scope, setScope } = useCloudConnectConfig(provider);

  const { script, isLoading, error, refetch } = useAssetDiscoveryCommandQuery({
    provider,
    scope,
  });

  useEffect(() => {
    onScriptStateChange?.({
      isLoading,
      hasError: !!error,
    });
  }, [isLoading, error, onScriptStateChange]);

  useEffect(() => {
    onProviderChange?.(provider);
  }, [provider, onProviderChange]);

  const handleProviderSelect = (nextProvider: ScriptMethodProvider) => {
    setProvider(nextProvider as Cloud);
  };

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: '100%',
        pl: 20,
        pr: 24,
        pt: 24,
        gap: 0,
      }}
    >
      <Stack direction="row" gap={16} alignItems="flex-start">
        <StepBadge number={1} hasConnector />
        <Stack gap={16} flex={1} pb={24}>
          <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
            {ONBOARDING_STEP_COPY.selectCloudProviderTitle}
          </Typography>
          <ProviderSelect
            selected={provider}
            onSelect={handleProviderSelect}
            showAnywhere={false}
          />
        </Stack>
      </Stack>

      <Stack direction="row" gap={16} alignItems="flex-start">
        <StepBadge number={2} />
        <Stack gap={16} flex={1} pb={24}>
          <Stack gap={4}>
            <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
              Run this script
            </Typography>
            <Typography
              variant="P14R"
              color={tokens.theme.fg.structural.secondary}
            >
              {ONBOARDING_STEP_COPY.cloudConnectScriptDescription}
            </Typography>
          </Stack>

          <ScopeToggle
            provider={provider}
            scope={scope}
            onScopeChange={setScope}
          />

          <AgentCodeSnippet
            code={script}
            isLoading={isLoading}
            refetch={refetch}
            error={error}
            errorMessage="Failed to load Cloud Connect script"
            testId="cloud-connect-script"
          />
        </Stack>
      </Stack>

      <Box sx={{ pl: 42, pb: 16 }}>
        <Divider />
      </Box>

      <Permissions permissions={providerToPermissions[provider]} />
    </Stack>
  );
};
